const DEFAULT_SEARCH_FILTERS = [
  { labelKey: "colour", value: "metafields.colour.colour_swatch_name" },
  { labelKey: "size", value: "sizesAvailable" },
  { labelKey: "price", value: "price" },
];

export function buildAlgoliaFilters({
  baseFilters = [],
  facets = {},
  includeRefinements = true,
  ranges = {},
} = {}) {
  const filters = baseFilters.filter(Boolean);

  if (!includeRefinements) {
    return filters.join(" AND ");
  }

  Object.entries(facets).forEach(([attribute, values]) => {
    if (!values.length) {
      return;
    }

    filters.push(
      `(${values
        .map((value) => `${attribute}:"${escapeAlgoliaFilterValue(value)}"`)
        .join(" OR ")})`,
    );
  });

  Object.entries(ranges).forEach(([attribute, range]) => {
    const hasMin =
      range.min !== null &&
      range.min !== undefined &&
      String(range.min).trim() !== "";
    const hasMax =
      range.max !== null &&
      range.max !== undefined &&
      String(range.max).trim() !== "";
    const min = Number(range.min);
    const max = Number(range.max);

    if (hasMin && Number.isFinite(min)) {
      filters.push(`${attribute} >= ${min}`);
    }

    if (hasMax && Number.isFinite(max)) {
      filters.push(`${attribute} <= ${max}`);
    }
  });

  return filters.join(" AND ");
}

export function defaultSearchFilterOptions(filterLabels = {}) {
  return DEFAULT_SEARCH_FILTERS.map((filter) => ({
    label: filterLabels[filter.labelKey] || filter.labelKey,
    value: filter.value,
  }));
}

export function resolveFilterOptions({
  filterOptions = [],
  includeFilter = () => true,
  marketHandle = "",
} = {}) {
  return filterOptions
    .map((filter) => {
      if (isPriceAttribute(filter.value)) {
        return {
          ...filter,
          value: `price.${marketHandle}.amount`,
        };
      }

      return filter;
    })
    .filter((filter) => filter.value)
    .filter(includeFilter);
}

export function isPriceAttribute(attribute) {
  return String(attribute).toLowerCase().includes("price");
}

export function rangeBounds(stats) {
  const min = 0;
  const max = Math.ceil((Number(stats.max || 0) * 1.25) / 5) * 5;

  return {
    min,
    max: Math.max(max, Math.ceil(stats.max || 0)),
  };
}

export function rangeTicks(min, max) {
  const step = (max - min) / 5;

  return Array.from({ length: 6 }, (_, index) =>
    String(Math.round(min + step * index)),
  );
}

export function rangeStyle(min, max, bounds) {
  return `--range-start: ${rangePercent(min, bounds)}%; --range-end: ${rangePercent(max, bounds)}%;`;
}

export function rangePercent(value, bounds) {
  const range = bounds.max - bounds.min;

  if (!range) {
    return 0;
  }

  return ((Number(value) - bounds.min) / range) * 100;
}

export function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

export function escapeAlgoliaFilterValue(value) {
  return String(value).replace(/\\/g, "\\\\").replace(/"/g, '\\"');
}
